import {globalError, IShowTeamPayload} from "modules/actions";
import {defaultLineup} from "modules/defaultValues/team";
import {call, put, select} from "typed-redux-saga";
import {Api, ApiError, PoolType} from "modules/utils";
import * as actions from "modules/actions";
import {
	getMyTeam,
	getFieldSelectedPosition,
	isSelectedEventLocked,
	getFilledTrades,
} from "modules/selectors";
import {
	ISagaAction,
	ISetUnitPositionPayload,
	ITeam,
	IShowMyTeamPayload,
	ITradeProcess,
} from "modules/types";
import {get, set, first, split, includes} from "lodash";
import i18n from "i18next";

export const fetchMyTeamSaga = function* ({payload}: ISagaAction<IShowMyTeamPayload>) {
	try {
		const response = yield* call(Api.FantasyTeam.show_my, {
			event_id: payload,
		});
		ApiError.CHECK(response);

		const team = get(response, "result.team", {});
		const lineup = get(response, "result.team.lineup", defaultLineup);

		yield* put(
			actions.fetchMyTeamSuccess({
				...team,
				lineup: !!lineup ? lineup : defaultLineup,
			})
		);
	} catch (e) {
		yield* put(actions.fetchMyTeamFailed(e as Error));
	}
};

export const postTeamAutoPickSaga = function* () {
	try {
		const {lineup} = yield* select(getMyTeam);
		const response = yield* call(Api.FantasyTeam.autopick, {lineup});
		ApiError.CHECK(response);
		yield* put(actions.postTeamAutoPickSuccess(response.result.team));
	} catch (e) {
		yield* put(actions.postTeamAutoPickFailed(e as Error));
	}
};

export const postMyTeamSaga = function* () {
	try {
		const {lineup, turbo_rider, start_event_id} = yield* select(getMyTeam);
		const is_actual_event_locked = yield* select(isSelectedEventLocked());

		const response = yield* call(Api.FantasyTeam.update, {
			lineup,
			turbo_rider: turbo_rider ? turbo_rider : undefined,
		});
		ApiError.CHECK(response);

		if (!start_event_id && is_actual_event_locked) {
			yield* put(
				globalError(
					i18n.t(
						"team.error.gp_locked",
						`Your team has been saved.
			 Please note: the currently live GP has already locked out,
			  and this team will start scoring from the next GP.
			   You can make unlimited changes until then.`
					) as string
				)
			);
		}
		yield* put(actions.postMyTeamSuccess(response.result.team));
		yield* put(actions.showCrossPromotionModal());
	} catch (e) {
		yield* put(actions.postMyTeamFailed(e as Error));
	}
};

export const postMakeTradesSaga = function* () {
	try {
		const trades = yield* select(getFilledTrades);
		const {id} = yield* select(getMyTeam);

		const payload = trades.map(({id_out, id_in, pool_type}: ITradeProcess) => {
			return {
				[`old_${pool_type}_id`]: id_out,
				[`new_${pool_type}_id`]: id_in,
			};
		});
		const response = yield* call(Api.FantasyTrade.make_trades, {
			team_id: id,
			trades: payload,
		});
		ApiError.CHECK(response);
		yield* put(actions.postMyTeamSuccess(response.result.team));
		yield* put(actions.postMakeTradesSuccess());
		yield* put(actions.clearTrade());
		yield* put(actions.showCrossPromotionModal());
	} catch (e) {
		yield* put(actions.postMyTeamFailed(e as Error));
	}
};

export const postMakeReverseSaga = function* () {
	try {
		const response = yield* call(Api.FantasyTrade.rollback);
		ApiError.CHECK(response);
		yield* put(actions.postMyTeamSuccess(response.result.team));
		yield* put(actions.cancelReverse());
		yield* put(actions.showCrossPromotionModal());
	} catch (e) {
		yield* put(actions.postMyTeamFailed(e as Error));
	}
};

export const showOverBudgetErrorSaga = function* () {
	yield* put(
		actions.globalError(
			i18n.t(
				"trade.modal.salary_cap",
				"You do not have enough remaining salary to make this selection."
			) as string
		)
	);
};

const positionsByType = {
	[PoolType.Idle]: [],
	[PoolType.Riders]: ["riders", "riders_reserve"],
	[PoolType.Constructors]: ["constructors"],
	[PoolType.Squads]: ["squads"],
};

export const setUnitToFieldSaga = function* ({
	payload: {id, type},
}: ISagaAction<ISetUnitPositionPayload>) {
	const {lineup}: ITeam = yield* select(getMyTeam);
	const new_lineup = JSON.parse(JSON.stringify(lineup));
	const selected_position = yield* select(getFieldSelectedPosition);
	const position_is_free = get(new_lineup, selected_position as string) === 0;
	const field_key = first(split(selected_position, "."));
	const is_add_type_same_as_selected = includes(positionsByType[type], field_key);
	const can_be_set_by_path =
		selected_position && is_add_type_same_as_selected && position_is_free;

	const setUnitToEmptyPositionByType = (type: PoolType) =>
		positionsByType[type].some((key) => {
			const index = (new_lineup[key] || []).indexOf(0);
			return index !== -1 ? set(new_lineup, `${key}.${index}`, id) : false;
		});

	if (can_be_set_by_path) {
		set(new_lineup, selected_position, id);
	} else {
		setUnitToEmptyPositionByType(type);
	}

	yield* put(actions.updateLineup(new_lineup));
	yield* put(actions.clearFieldPositionSelection());
};

export const getUserTeamSaga = function* ({payload}: ISagaAction<IShowTeamPayload>) {
	try {
		const response = yield* call(Api.FantasyTeam.show, payload);
		ApiError.CHECK(response);
		yield* put(actions.getUserTeamSuccess(response.result.team));
	} catch (e) {
		yield* put(actions.getUserTeamFailed(e as Error));
	}
};
